import { gql, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/browser';
import { message } from 'antd';
import React, { useMemo, useState } from 'react';
import { Spinner } from '../../../components';
import Scheduling from '../components/Scheduling/Scheduling';
import { useQueryParam, StringParam } from 'use-query-params';
import { useAppContext } from '../../../hooks';
import { useGetPatientQuery } from '../../../generated/graphql';

// The form only needs these cheap organization fields to lay itself out, so it waits for
// this query alone. The provider, specialty and payor lists start at the same time but load
// in the background - see schedulingProvidersQuery and friends below.
export const schedulingSettingsQuery = gql`
  query schedulingSettingsQuery($locationId: ID, $organizationId: ID!) {
    getOrganization(organizationId: $organizationId) {
      id
      name
      useClickToSearchForPatients
      showInsuranceFiltersDuringScheduling
      groups(locationId: $locationId, isReferralNetwork: true, isActive: true) {
        id
        name
      }
      consumerSchedulingSettings {
        id
        formFieldSettings {
          field
          required
        }
      }
      preAuthorizedSpecialties
      preAuthorizedProcedures
    }
  }
`;

// The sender dropdown searches on the server rather than downloading every provider: it
// shows the first PROVIDER_PAGE_SIZE by name, and typing asks for matches instead.
export const PROVIDER_PAGE_SIZE = 50;

export const schedulingProvidersQuery = gql`
  query schedulingProvidersQuery($locationId: ID, $organizationId: ID!, $search: String, $limit: Int) {
    getOrganization(organizationId: $organizationId) {
      id
      providers(locationId: $locationId, search: $search, limit: $limit) {
        id
        name
        npi
        location {
          id
          name
        }
      }
    }
  }
`;

// A providerId in the URL may be outside the first page of providers, so its name is
// looked up on its own - otherwise the sender dropdown would show the raw id.
export const schedulingSelectedProviderQuery = gql`
  query schedulingSelectedProviderQuery($id: ID!) {
    getProvider(getProviderInput: { id: $id }) {
      id
      name
    }
  }
`;

export const schedulingSpecialtiesQuery = gql`
  query schedulingSpecialtiesQuery($organizationId: ID!) {
    listSpecialtiesWithProfiles(organizationId: $organizationId) {
      id
      name
      procedures {
        id
        name
      }
      # Prevent GraphQL from sharing the object cache of these specialities / procedures, because the result of
      # the procedures list depends on if it's called with listSpecialtiesWithprofiles or listSpecialities and cause
      # the GraphQL cache to be incorrect depending on which query is called first. In the future, the lists should
      # have different field names or be a flag on the procedure so that the cache can be shared.
      __typename @skip(if: true)
    }
  }
`;

export const schedulingPayorsQuery = gql`
  query schedulingPayorsQuery($organizationId: ID!) {
    getPayorsAndPlansByOrganization(organizationId: $organizationId) {
      id
      name
      payorPlans {
        id
        name
      }
    }
  }
`;

// The lists load behind the form, so a failure would otherwise just look like an empty
// dropdown - leaving a required field like the sender impossible to fill with no clue why.
const handleListError = listName => error => {
  Sentry.captureException(error);
  message.error(`We couldn't load the ${listName} list. Please refresh the page and try again.`);
};

const SchedulingContainer = props => {
  const [preselectedPatientId] = useQueryParam('patientId', StringParam);
  const [prefilledProviderId] = useQueryParam('providerId', StringParam);
  const [providerSearch, setProviderSearch] = useState('');
  const { currentLocation, currentOrganization } = useAppContext();

  const organizationId = currentOrganization.id;
  const locationId = currentLocation ? currentLocation.id : null;

  const { data: patientQueryData, loading: patientLoading } = useGetPatientQuery({
    variables: {
      id: preselectedPatientId,
      organizationId,
    },
    skip: preselectedPatientId === undefined,
  });

  const { data: settingsData, error: settingsError } = useQuery(schedulingSettingsQuery, {
    variables: { locationId, organizationId },
  });

  // The dropdown lists load in the background: the form renders without them, and they
  // are usually in by the time the user opens a dropdown. Changing providerSearch re-runs
  // the providers query with the new variables.
  const providersResult = useQuery(schedulingProvidersQuery, {
    variables: { locationId, organizationId, search: providerSearch, limit: PROVIDER_PAGE_SIZE },
    // With a defaultProviderId there is no sender dropdown to fill.
    skip: Boolean(props.defaultProviderId),
    onError: handleListError('provider'),
  });
  const { data: prefilledProviderData } = useQuery(schedulingSelectedProviderQuery, {
    variables: { id: prefilledProviderId },
    skip: !prefilledProviderId,
  });
  const specialtiesResult = useQuery(schedulingSpecialtiesQuery, {
    variables: { organizationId },
    onError: handleListError('specialty'),
  });
  const payorsResult = useQuery(schedulingPayorsQuery, {
    variables: { organizationId },
    onError: handleListError('insurance carrier'),
  });

  const organizationSettings = settingsData && settingsData.getOrganization;
  // Keep showing the last list while a new search is in flight instead of flashing empty.
  const providersData = providersResult.data || providersResult.previousData;

  const organization = useMemo(
    () => ({
      id: organizationId,
      name: currentOrganization.name,
      ...organizationSettings,
      groups: (organizationSettings && organizationSettings.groups) || [],
      providers: (providersData && providersData.getOrganization && providersData.getOrganization.providers) || [],
    }),
    [organizationId, currentOrganization.name, organizationSettings, providersData]
  );

  if (patientLoading) return null;
  if (settingsError) return <div>Error</div>;
  // Several fields change shape with these settings (the patient field's search mode, the
  // insurance and network groups sections), so the form waits for them rather than
  // rearranging itself mid-use.
  if (!organizationSettings) return <Spinner />;

  const specialties = (specialtiesResult.data && specialtiesResult.data.listSpecialtiesWithProfiles) || [];
  const payors = (payorsResult.data && payorsResult.data.getPayorsAndPlansByOrganization) || [];

  return (
    <Scheduling
      organization={organization}
      specialties={specialties}
      payors={payors}
      providersLoading={providersResult.loading}
      providersHasMore={organization.providers.length >= PROVIDER_PAGE_SIZE}
      providerSearch={providerSearch}
      onProviderSearch={setProviderSearch}
      prefilledProvider={prefilledProviderData && prefilledProviderData.getProvider}
      specialtiesLoading={specialtiesResult.loading}
      payorsLoading={payorsResult.loading}
      featureFlags={currentOrganization.featureFlags}
      preselectedPatient={patientQueryData && patientQueryData.getPatient}
      {...props}
    />
  );
};

export default SchedulingContainer;
